type WorkspaceData = { ownerId?: unknown; ownerIds?: unknown } | null | undefined

/**
 * getWorkspaceOwnerIds — returns the de-duplicated owner uid list.
 *
 * Requirements:
 *  - no ownerIds → [ownerId]
 *  - ownerIds present → [ownerId, ...ownerIds] de-duplicated, string entries only
 *  - non-strings and empty strings dropped
 */
export const getWorkspaceOwnerIds = (workspaceData?: WorkspaceData): string[] => {
  if (!workspaceData) return []
  const ownerId = typeof workspaceData.ownerId === 'string' && workspaceData.ownerId.trim().length > 0
    ? workspaceData.ownerId.trim()
    : null
  const rawOwnerIds = Array.isArray(workspaceData.ownerIds) ? workspaceData.ownerIds : null
  const extras: string[] = rawOwnerIds
    ? rawOwnerIds.filter((v): v is string => typeof v === 'string' && v.trim().length > 0).map((v) => v.trim())
    : []
  const seen = new Set<string>()
  const result: string[] = []
  for (const uid of [ownerId, ...extras]) {
    if (uid !== null && !seen.has(uid)) {
      seen.add(uid)
      result.push(uid)
    }
  }
  return result
}

/**
 * isWorkspaceOwner — returns true if uid is in the workspace's owner set.
 * Returns false if uid is empty, whitespace-only, null, or undefined.
 */
export const isWorkspaceOwner = (workspaceData?: WorkspaceData, uid?: string | null): boolean => {
  if (!uid || typeof uid !== 'string' || uid.trim().length === 0) return false
  return getWorkspaceOwnerIds(workspaceData).includes(uid.trim())
}
